'use client';
import { createContext, useContext, type ReactNode } from 'react';
import { Popconfirm } from 'antd';
import { toast } from 'react-hot-toast';
import { FaCheck } from 'react-icons/fa6';
import { IoClose } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';

import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { WatchlistManager, WatchlistSymbol } from '@benzinga/watchlist-manager';
import { abbreviateNumber, formatTickerToURLFriendly } from '@benzinga/utils';
import type { RankingDetail } from '@benzinga/quotes-manager';

import { TickerLogo } from '../../../../edge/dashboard/_components/TickerLogo';
import { TableCellSkeleton } from '../WatchlistInsightsSkeleton';
import { WATCHLIST_QUOTES_SYMBOL_CELL_CLASS } from '../portfolioLayoutConstants';

const ResearchTableLoadingContext = createContext({ isEnriching: false });

export const ResearchTableLoadingProvider = ({
  children,
  isEnriching,
}: {
  children: ReactNode;
  isEnriching: boolean;
}) => (
  <ResearchTableLoadingContext.Provider value={{ isEnriching }}>{children}</ResearchTableLoadingContext.Provider>
);

export const useResearchTableLoading = () => useContext(ResearchTableLoadingContext);

export const ResearchCell = ({
  children,
  empty = '-',
  hasValue,
  isPending,
  skeletonClassName = 'h-4 w-16',
}: {
  children: ReactNode;
  empty?: ReactNode;
  hasValue: boolean;
  isPending?: boolean;
  skeletonClassName?: string;
}) => {
  const { isEnriching } = useResearchTableLoading();
  const pending = isPending ?? isEnriching;

  if (hasValue) {
    return <>{children}</>;
  }

  if (pending) {
    return <TableCellSkeleton className={skeletonClassName} />;
  }

  return <span className="block min-h-4 text-sm leading-4">{empty}</span>;
};

export const RESEARCH_SYMBOL_COLUMN_WIDTH = 184;

const TickerLogoLookupContext = createContext<Record<string, string>>({});

export const TickerLogoLookupProvider = ({
  children,
  logos,
}: {
  children: ReactNode;
  logos: Record<string, string>;
}) => <TickerLogoLookupContext.Provider value={logos}>{children}</TickerLogoLookupContext.Provider>;

export const useTickerLogoLookup = () => useContext(TickerLogoLookupContext);

export const SymbolCell = ({ record }) => {
  const { selectedWatchlist, setDeleteTickerModal } = useWatchlistContext();
  const session = useContext(SessionContext);
  const logoBySymbol = useTickerLogoLookup();

  const handleDelete = async () => {
    if (!selectedWatchlist) return;

    if (selectedWatchlist.watchlistId === 'overview') {
      setDeleteTickerModal({ show: true, ticker: record.symbol, isLoading: false });
      return;
    }

    const watchlistManager = session.getManager(WatchlistManager);
    const watchlistTicker = selectedWatchlist.symbols.find(symbol => symbol.symbol === record.symbol);
    if (!watchlistTicker) {
      toast.error(`${record.symbol} not found in watchlist`);
      return;
    }

    const result = await watchlistManager.removeTickersFromWatchlist(selectedWatchlist, [
      watchlistTicker as WatchlistSymbol,
    ]);
    if (result.ok) {
      toast.success(
        `${record?.symbol ?? record?.quote?.symbol ?? record?.financials?.company?.primarySymbol ?? '-'} removed from watchlist`,
      );
    }
  };

  const symbol = record?.symbol ?? record?.quote?.symbol ?? record?.financials?.company?.primarySymbol ?? null;
  const companyName =
    record?.quote?.type === 'CRYPTO'
      ? record?.quote?.description
      : (record?.quote?.companyStandardName ?? record?.financials?.company?.standardName);

  const quoteHref = symbol ? `/quote/${formatTickerToURLFriendly(symbol)}` : '#';

  return (
    <div className="group flex min-w-0 items-center gap-2">
      {symbol ? (
        <a className="shrink-0" href={quoteHref}>
          <TickerLogo size={20} src={logoBySymbol[symbol]} symbol={symbol} />
        </a>
      ) : null}
      <div className={`${WATCHLIST_QUOTES_SYMBOL_CELL_CLASS} min-w-0 flex-1`}>
        <div className="flex min-w-0 items-center gap-1">
          <a
            className="min-w-0 truncate font-extrabold leading-5 text-bzblue-900 group-hover:underline"
            href={quoteHref}
          >
            {symbol ?? '—'}
          </a>
          <Popconfirm title="Are you sure you want to remove this ticker?" onConfirm={handleDelete}>
            <button
              type="button"
              aria-label={symbol ? `Remove ${symbol}` : 'Remove ticker'}
              className="inline-flex size-5 shrink-0 items-center justify-center rounded text-slate-700 opacity-0 transition-[opacity,color] duration-150 hover:text-red-600 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
              onClick={event => event.stopPropagation()}
            >
              <IoClose size={16} aria-hidden />
            </button>
          </Popconfirm>
        </div>
        {companyName ? (
          <div className="mt-0.5 w-full truncate text-xs leading-4 text-slate-400">{companyName}</div>
        ) : null}
      </div>
    </div>
  );
};

export const RankingTrendCell = ({ value }: { value: 'Y' | 'N' | undefined }) => {
  const { isEnriching } = useResearchTableLoading();

  if (!value) {
    if (isEnriching) {
      return <TableCellSkeleton className="ml-auto h-6 w-6 rounded-md" />;
    }

    return <div className="inline-flex h-6 min-h-6 items-center">-</div>;
  }

  return (
    <div
      className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${
        value === 'Y' ? 'bg-bzgreen-100 text-bzgreen-700' : 'bg-bzred-100 text-bzred-600'
      }`}
    >
      {value === 'Y' ? <FaCheck className="h-3 w-3" /> : <MdClose className="h-3.5 w-3.5" />}
    </div>
  );
};

export type RankingScoreField = 'momentum' | 'growth' | 'value' | 'quality';

export type RankingScoreDisplay =
  | { kind: 'unknown' }
  | { kind: 'unavailable' }
  | { kind: 'value'; value: number };

export const getRankingScoreDisplay = (
  rankings: RankingDetail | null | undefined,
  field: RankingScoreField,
): RankingScoreDisplay => {
  if (rankings == null) {
    return { kind: 'unknown' };
  }
  if (rankings.exists === false) {
    return { kind: 'unavailable' };
  }
  const score = rankings[field];
  if (score == null) {
    return { kind: 'unknown' };
  }
  return { kind: 'value', value: score };
};

export const RankingScoreCell = ({
  rankings,
  field,
}: {
  rankings: RankingDetail | null | undefined;
  field: RankingScoreField;
}) => {
  const { isEnriching } = useResearchTableLoading();
  const display = getRankingScoreDisplay(rankings, field);
  if (display.kind === 'unknown') {
    if (isEnriching) {
      return <TableCellSkeleton className="h-4 w-8" />;
    }

    return <span className="block min-h-4 text-sm leading-4">-</span>;
  }
  if (display.kind === 'unavailable') {
    return <span className="text-slate-400">N/A</span>;
  }
  return <span>{display.value}</span>;
};

export const rankingScoreSortValue = (
  rankings: RankingDetail | null | undefined,
  field: RankingScoreField,
): number => {
  const display = getRankingScoreDisplay(rankings, field);
  return display.kind === 'value' ? display.value : -1;
};

export const LinkPortfolioCell = () => {
  const { setShowYodleeModal } = useWatchlistContext();
  return (
    <div
      className="text-blue-500 cursor-pointer text-center text-xs whitespace-nowrap"
      onClick={() => {
        setShowYodleeModal(true);
      }}
    >
      Link Portfolio
    </div>
  );
};

export const trendValueFormatter = (value: string | undefined) => {
  if (!value) return -1;
  return value === 'Y' ? 1 : 0;
};

export const formatCurrency = (value: number | undefined): string => {
  if (value === undefined || value === null) return '-';
  return abbreviateNumber(value, { currency: 'USD' });
};

export const formatPercentage = (value: number | undefined): string => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

export const renderColoredValue = (
  value: number | undefined | null,
  format: 'currency' | 'percentage' = 'currency',
) => {
  if (value === undefined || value === null) return '-';

  const formattedValue = format === 'currency' ? formatCurrency(value) : formatPercentage(value);
  const color = value >= 0 ? '#16a34a' : '#dc2626';

  return <span style={{ color }}>{formattedValue}</span>;
};

export const renderRange = (low: number | undefined, high: number | undefined, last: number | undefined) => {
  if (low === undefined || high === undefined || high <= low) return '-';

  const current = last ?? low;
  const position = Math.min(100, Math.max(0, ((current - low) / (high - low)) * 100));

  return (
    <div className="flex h-6 min-w-28 w-full items-center gap-1.5 text-xs tabular-nums text-bzblue-900">
      <span className="shrink-0 text-slate-500">{low.toFixed(2)}</span>
      <div
        className="relative h-1.5 min-w-10 flex-1 rounded-full bg-slate-200"
        title={last != null ? `Last: ${last.toFixed(2)}` : undefined}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-bzblue-700"
          style={{ width: `${position}%` }}
        />
        <div
          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-bzblue-700 shadow-sm ring-1 ring-bzblue-700/30"
          style={{ left: `${position}%` }}
        />
      </div>
      <span className="shrink-0 text-slate-500">{high.toFixed(2)}</span>
    </div>
  );
};

export const renderAction = (action: string | undefined) => {
  if (!action) return '-';

  const actionLower = action.toLowerCase();
  let color = '#6b7280';

  if (actionLower.includes('upgrade') || actionLower.includes('initiate') || actionLower.includes('raise')) {
    color = '#22c55e';
  } else if (actionLower.includes('downgrade') || actionLower.includes('lower') || actionLower.includes('cut')) {
    color = '#ef4444';
  }

  return <span style={{ color }}>{action}</span>;
};
